// PartA3.js - Part A3 Time Management component with pagination

import React, { useState, useEffect } from 'react';
import { Button, message, Pagination } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { getAccessToken } from '../../authClient';
import PartA3TimeManagement from './components/PartA3TimeManagement';
import PartA3TimeMap from './components/PartA3TimeMap';
import { API_CONFIG } from '../../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

const PartA3 = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    // Form data state matching the Part A3 images exactly
    const [formData, setFormData] = useState({
        // Time Management Statements (Rarely, Sometimes, Usually, Almost always)
        follow_schedule: '',
        get_distracted: '',
        last_minute: '',
        figure_time: '',
        decide_tasks: '',

        // My Time Habits
        best_time_study: '',
        best_time_play: '',
        best_time_relax: '',
        best_time_friends: '',

        time_schoolwork: '',
        time_fitness: '',
        time_other_things: '',
        time_emotional_wellbeing: '',

        make_todo_lists: '',
        improve_fitness: '',

        // Time Preferences
        spend_more_time_on: '',
        spend_less_time_on: '',

        // Time Map - AM/PM activities (this would be more complex in a real implementation)
        time_map_activities: [],

        // Follow-up questions
        activity_more_time: '',
        activity_less_time: '',
        peer_feedback: ''
    });

    useEffect(() => {
        loadExistingData();
    }, []);

    const loadExistingData = async () => {
        try {
            const token = getAccessToken();
            const response = await fetch(`${API_BASE_URL}/secondary/user/hpc-profile/part-a3`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.exists && data.part_a_3) {
                    setFormData(prevData => ({
                        ...prevData,
                        ...data.part_a_3
                    }));
                }
            }
        } catch (error) {
            console.error('Error loading existing data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = getAccessToken();
            const response = await fetch(`${API_BASE_URL}/secondary/user/hpc-profile/part-a3`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                message.success('Part A3 saved successfully!');
            } else {
                throw new Error('Failed to save Part A3');
            }
        } catch (error) {
            console.error('Error saving Part A3:', error);
            message.error('Failed to save Part A3');
        } finally {
            setSaving(false);
        }
    };

    const renderCurrentPage = () => {
        switch (currentPage) {
            case 1:
                return (
                    <PartA3TimeManagement
                        formData={formData}
                        handleInputChange={handleInputChange}
                    />
                );
            case 2:
                return (
                    <PartA3TimeMap
                        formData={formData}
                        handleInputChange={handleInputChange}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="part-a3-container">
            {/* Page Content */}
            {renderCurrentPage()}

            {/* Navigation and Save Controls */}
            <div className="page-controls">
                <div className="pagination-section">
                    <Pagination
                        current={currentPage}
                        total={2}
                        pageSize={1}
                        onChange={setCurrentPage}
                        showSizeChanger={false}
                        showQuickJumper={false}
                        showTotal={(total, range) => `Page ${range[0]} of ${total}`}
                    />
                </div>

                <div className="save-section">
                    <Button
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={saving}
                        className="save-button"
                    >
                        {saving ? 'Saving...' : 'Save Part A3'}
                    </Button>

                    <Button
                        size="large"
                        icon={<ReloadOutlined />}
                        onClick={loadExistingData}
                        className="reload-button"
                        style={{ marginLeft: '10px' }}
                    >
                        Reload Data
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PartA3;